import React, { useEffect, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { prospects } from '../../api';
import { errMsg } from './constants';

export const TemplatesDialog = ({ open, onOpenChange, toast }) => {
  const [data, setData] = useState(null);
  const [key, setKey] = useState('engagement');
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) prospects.templates().then(setData); }, [open]);
  const cur = data?.templates.find((t) => t.key === key);
  useEffect(() => { if (cur) setDraft({ title: cur.title, body: cur.body }); }, [cur]);
  const replace = (t) => setData((d) => ({ ...d, templates: d.templates.map((x) => (x.key === t.key ? t : x)) }));

  const save = () => {
    setBusy(true);
    prospects.saveTemplate(key, draft).then((t) => { replace(t); toast({ title: 'Template saved', description: t.title }); })
      .catch((e) => toast({ title: 'Could not save', description: errMsg(e), variant: 'destructive' })).finally(() => setBusy(false));
  };
  const reset = () => window.confirm('Reset this template to the Accu Desk default?') &&
    prospects.resetTemplate(key).then((t) => { replace(t); toast({ title: 'Template reset to default' }); });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="templates-dialog">
        <DialogHeader><DialogTitle>Engagement & contract templates</DialogTitle></DialogHeader>
        {!data || !draft ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div> : (
          <div className="grid md:grid-cols-[220px_1fr] gap-5">
            <div className="space-y-1">
              {data.templates.map((t) => (
                <button key={t.key} onClick={() => setKey(t.key)} data-testid={`template-tab-${t.key}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm ${t.key === key ? 'bg-emerald-50 text-emerald-900 font-semibold' : 'hover:bg-stone-50 text-stone-700'}`}>
                  {t.title}{t.customised && <span className="ml-1 text-[10px] uppercase text-emerald-700">· custom</span>}
                </button>
              ))}
              <div className="pt-4 text-[11px] text-stone-500 leading-relaxed">
                <div className="font-semibold text-stone-700 mb-1">Merge fields</div>
                {data.mergeFields.map((m) => <code key={m} className="block">{`{{${m}}}`}</code>)}
                <div className="mt-2">Use <code># Title</code>, <code>## Heading</code> and <code>- bullet</code> lines.</div>
              </div>
            </div>
            <div className="space-y-3">
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="h-9 font-semibold" data-testid="template-title-input"/>
              <Textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={22} className="font-mono text-xs leading-relaxed" data-testid="template-body-input"/>
              <div className="flex justify-between">
                <Button variant="outline" size="sm" onClick={reset} disabled={!cur.customised} data-testid="template-reset-btn"><RotateCcw size={13} className="mr-1.5"/> Reset to default</Button>
                <Button size="sm" onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="template-save-btn">{busy && <Loader2 size={13} className="mr-1.5 animate-spin"/>} Save template</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
